import { useEffect } from "react";

/**
 * Subscribe to a DOM CustomEvent dispatched on `window`.
 * Handles addEventListener + removeEventListener boilerplate automatically.
 *
 * Usage:
 *   useBiostreamEvent<{ msg: string }>("biostream:ble-debug", ({ msg }) => console.log(msg));
 *
 * The handler is re-registered whenever deps change (same semantics as useEffect deps).
 * Pass a stable callback (useCallback) or list the mutable values in deps to avoid
 * stale-closure bugs.
 */
export function useBiostreamEvent<T = unknown>(
  eventName: string,
  handler: (detail: T) => void,
  deps: unknown[] = [],
): void {
  useEffect(() => {
    const listener = (e: Event) => handler((e as CustomEvent<T>).detail);
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }, [eventName, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
}
